import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LocalStorageService } from './connectors/localstorage';
import { UserBehaviorService } from './services/user-events/user-behavior.service';
import { AccountInfo } from '@azure/msal-browser';
import { menuItems } from './menu-items';
import { LogoutService } from './services/auth/logout.service';
import { AuthService } from './services/auth/authService.service';
import { map, filter, take, timeout, catchError } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MicrosoftLoginGuard implements CanActivate {
  deviceInfo: any;

  constructor(
    private authService: MsalService,
    private userBehavior: UserBehaviorService,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private localStorage: LocalStorageService,
    private logoutService: LogoutService,
    private appAuthService: AuthService
  ) {
    this.epicFunction();
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const loggedByMsal = this.authService.instance.getActiveAccount();

    if (loggedByMsal == null) {
      this.reset();
      this.router.navigate(['/login']);
      return false;
    }

    this.setMsalLoggedInUser(loggedByMsal);

    /** Prioritize the saved return URL when landing on login or home. */
    const isLandingPage = state.url.startsWith('/home') || state.url === '/' || state.url.startsWith('/login');
    const returnUrl = this.logoutService.getReturnUrl();
    
    if (returnUrl && isLandingPage && returnUrl !== state.url && returnUrl !== '/login') {
      this.logoutService.consumeReturnUrl();
      this.router.navigateByUrl(returnUrl);
      return false; 
    }

    /** Wait for login and access data before allowing navigation. */
    return combineLatest([
      this.appAuthService.loginCompleted$.pipe(filter(completed => completed)),
      this.userBehavior.localStorageEvents$.pipe(filter(events => events.roles && events.teams))
    ]).pipe(
      timeout(5000),
      take(1),
      map(() => {
        return this.checkRouteAccess(route, state);
      }),
      catchError(err => {
        return of(this.checkRouteAccess(route, state));
      })
    );
  }

  private checkRouteAccess(
    route: ActivatedRouteSnapshot, 
    state: RouterStateSnapshot
  ): boolean {
    const rawRoles = this.localStorage.decryptAndGet('roles-access');
    const rawTeams = this.localStorage.decryptAndGet('teams-access');
    const roles: any[] = Array.isArray(rawRoles) ? rawRoles : [];
    const teams: any[] = Array.isArray(rawTeams) ? rawTeams : [];

    /** Block denied roles and teams. */
    if (route.data['denied'] != undefined && route.data['denied'] != null) {
      const hasDeniedRole = roles.some((role: any) => route.data['denied'].includes(role));
      const hasDeniedTeam = teams.some((team: any) => route.data['denied'].includes(team));

      if (hasDeniedRole || hasDeniedTeam) {
        this.router.navigate(['/access-denied']);
        return false;
      }
    }

    /** Check access from the menu configuration. */
    const currentLink = state.url
      .split(/[?#]/)[0]
      .replace(/^\/+|\/+$/g, '')
      .trim();

    const requiredAccess = this.findRequiredAccess(menuItems, currentLink);

    if (requiredAccess && requiredAccess.length > 0) {
      const hasAccess =
        roles.some((role: any) => requiredAccess.includes(role)) ||
        teams.some((team: any) => requiredAccess.includes(team));

      if (!hasAccess) {
        this.router.navigate(['/access-denied']);
        return false;
      }

      return true;
    }

    /** Fallback for legacy routes. */
    if (route.data['required'] != undefined && route.data['required'] != null) {
      const hasAccess =
        roles.some((role: any) => route.data['required'].includes(role)) ||
        teams.some((team: any) => route.data['required'].includes(team));

      if (!hasAccess) {
        this.router.navigate(['/access-denied']);
        return false;
      }
    }

    return true;
  }

  private findRequiredAccess(items: any, targetLink: string): string[] | null {
    if (!items) return null;

    let safeItems = items;
    if (!Array.isArray(items) && items.menuItems) {
      safeItems = items.menuItems;
    } else if (!Array.isArray(items) && items.default) {
      safeItems = items.default;
    }

    const menuArray = Array.isArray(safeItems) ? safeItems : [safeItems];

    for (const item of menuArray) {
      if (Array.isArray(item)) {
        const foundInArray = this.findRequiredAccess(item, targetLink);
        if (foundInArray) return foundInArray;
        continue;
      }

      /** Skip empty links. */
      if (typeof item.link === 'string' && item.link.trim() !== '') {
        const cleanItemLink = item.link.replace(/^\/+|\/+$/g, '').trim();

        if (cleanItemLink === targetLink && Array.isArray(item.required) && item.required.length > 0) {
          return item.required;
        }
      }

      /** Search nested menu items. */
      const possibleChildrenProps = ['items', 'child', 'menuShortcuts', 'areas', 'children'];

      for (const prop of possibleChildrenProps) {
        if (item[prop] && Array.isArray(item[prop]) && item[prop].length > 0) {
          const foundInChild = this.findRequiredAccess(item[prop], targetLink);
          if (foundInChild) {
            return foundInChild;
          }
        }
      }
    }

    return null;
  }

  reset(): void {
    this.userBehavior.setUser({
      name: '',
      email: '',
      from: ''
    });

    this.userBehavior.resetLocalStorageEvent();

    this.localStorage.remove('teams-access');
    this.localStorage.remove('roles-access');
    this.localStorage.remove('profile');
    this.localStorage.remove('userId');
  }

  setMsalLoggedInUser(user: AccountInfo): void {
    if (!user) {
      return;
    }

    this.userBehavior.setUser({
      name: user.name,
      email: user.username,
      from: 'msal'
    });
  }

  epicFunction() {
    this.deviceInfo = this.deviceService.getDeviceInfo();
  }
}
